import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { VariablePill } from '@/components/admin/variables/variable-pill';
import type { ConditionPalette as ConditionPaletteContent } from '@/lib/display-conditions';
import type { DisplayCondition } from '@matter_extractor/shared';
import { Search } from 'lucide-react';
import { type KeyboardEvent, useId, useState } from 'react';

/**
 * « Ajouter une condition » : les variables en portée, par provenance, et ce qui
 * n'est pas proposé (refonte modulaire §9.2, H60, H61 ; maquette
 * `ConditionsParNature`, « Ajouter une condition » ; issue #150).
 *
 * What it offers is `conditionPalette` of `lib/display-conditions.ts`, pure and
 * tested, and that function reads `variableReach` of `shared` - the rule the
 * save refuses on. The component draws it and nothing more: a search field
 * that filters as one types, three groups (the noyau, the objectives, the
 * fields and choices), each line wearing the pill the condition will cite, and
 * the foot that says what is not offered - a criterion never, a variable of
 * this page or a later one never (H61).
 *
 * It is drawn IN the inspector's column rather than floated over it: the column
 * scrolls, a floating panel would have to follow it, and the gesture it answers
 * is « add to this block », which the column already frames.
 */

export interface ConditionPaletteProps {
  /** The palette for what has been typed - `conditionPalette` bound to the block. */
  readonly palette: (query: string) => ConditionPaletteContent;
  readonly onPick: (condition: DisplayCondition) => void;
  readonly onClose: () => void;
}

export function ConditionPalette({ palette, onPick, onClose }: ConditionPaletteProps) {
  const searchId = useId();
  const [query, setQuery] = useState('');
  const content = palette(query);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  return (
    <div
      data-testid={PAGE_EDITOR_IDS.conditionPalette}
      onKeyDown={onKeyDown}
      className="space-y-2 rounded-md border-2 border-gray-200 bg-white p-2 shadow-lg"
    >
      <label htmlFor={searchId} className="flex items-center gap-2 border-gray-200 border-b pb-2">
        <Search aria-hidden className="size-4 shrink-0 text-gray-500" />
        <span className="sr-only">Chercher une variable</span>
        <input
          id={searchId}
          // biome-ignore lint/a11y/noAutofocus: the panel opens on a click that asks to type a name.
          autoFocus
          value={query}
          placeholder="Chercher une variable"
          onChange={(event) => setQuery(event.currentTarget.value)}
          className="h-8 min-w-0 flex-1 text-sm focus-visible:outline-none"
        />
      </label>

      {content.groups.length === 0 ? (
        <p className="px-1 text-gray-700 text-xs leading-4">
          {query === ''
            ? 'Aucune variable à citer ici : un input, une checkbox ou un choix d’une page d’avant en pose une, et le protocole apporte ses objectifs après la page protocole.'
            : 'Aucune variable de ce nom à citer ici.'}
        </p>
      ) : (
        content.groups.map((group) => (
          <div key={group.title} className="space-y-1">
            <p className="px-1 font-semibold text-gray-500 text-xs uppercase tracking-wide">
              {group.title}
            </p>
            <ul className="space-y-0.5">
              {group.options.map((option) => (
                <li key={option.key}>
                  <button
                    type="button"
                    data-testid={PAGE_EDITOR_IDS.conditionOption}
                    data-variable={option.key}
                    disabled={option.taken}
                    onClick={() => onPick(option.condition)}
                    className="flex w-full cursor-pointer items-baseline justify-between gap-3 rounded-sm px-1.5 py-1 text-left hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    {/* The NAME is what is looked for, so the pill keeps its width
                        and the provenance gives way when the line is short. */}
                    <VariablePill
                      paint={option.paint}
                      interactive={false}
                      className="max-w-full shrink-0 truncate text-xs leading-5"
                    />
                    <span className="min-w-0 truncate text-right text-gray-600 text-xs">
                      {option.taken ? 'déjà une condition' : option.meta}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}

      <div className="space-y-1 border-gray-200 border-t px-1 pt-2 text-gray-600 text-xs leading-4">
        {content.footer.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}
